import { create } from 'zustand';
import { supabase } from '../supabase';
import { persistClientReferenti } from '../utils/persistClientContacts';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useWhatsAppChatStore } from './whatsappChatStore';
import { isOrgAiEnabledNow, notifyAiDisabled } from '../hooks/useOrgAiEnabled';
import type {
  ChatAssignment,
  NewChatAssignment,
  AgentRequest,
  AgentRun,
  AgentRequestFilters,
  AgentRequestStatus
} from '../types/whatsappAgent';

// Module-level: canale Realtime per risultati analisi + flag smart analyze
let _activeAnalysisChannel: RealtimeChannel | null = null;
let _smartAnalyzeRan = false;

// Summary AI per una chat (cache dall'assignment + storico)
export interface AiSummary {
  text: string;
  nextSteps?: string[];
  generatedAt: string;
  lastMessageId: string;
  requestsCount: number;
}

interface WhatsAppAgentStore {
  // State
  assignments: ChatAssignment[];
  requests: AgentRequest[];
  runs: AgentRun[];
  loading: boolean;
  error: string | null;

  // Chat suggestions (for in-chat display)
  chatSuggestions: AgentRequest[];
  chatSuggestionsLoading: boolean;

  // AI Analysis state
  analysisStatus: Map<string, 'analyzing' | 'done'>;
  pendingSuggestionCounts: Map<string, number>;
  chatSummaries: Map<string, AiSummary>;

  // Draft text injected by agent suggestion into MessageInput
  pendingDraftText: string | null;
  setPendingDraftText: (text: string | null) => void;

  // Assignments
  fetchAssignments: (organizationId: string) => Promise<void>;
  createAssignment: (data: NewChatAssignment) => Promise<ChatAssignment | null>;
  updateAssignment: (id: string, data: Partial<ChatAssignment>) => Promise<void>;
  deleteAssignment: (id: string) => Promise<void>;

  // Requests
  fetchRequests: (organizationId: string, filters?: AgentRequestFilters) => Promise<void>;
  fetchProjectRequests: (projectId: string) => Promise<void>;
  updateRequestStatus: (id: string, status: AgentRequestStatus, reviewedBy?: string) => Promise<void>;
  createTaskFromRequest: (requestId: string, projectId: string, assignTo?: string) => Promise<string | null>;
  dismissRequest: (id: string, userId: string) => Promise<void>;
  toggleScope: (requestId: string, isOutOfScope: boolean, note?: string) => Promise<void>;
  sendClarification: (requestId: string, message: string) => Promise<boolean>;

  // Chat suggestions
  fetchChatSuggestions: (chatJid: string, organizationId: string) => Promise<void>;
  sendSuggestion: (requestId: string, customText?: string) => Promise<boolean>;
  dismissSuggestion: (requestId: string, userId: string) => Promise<void>;
  createTasksFromSuggestions: (requestIds: string[], projectId: string, assignTo?: string, overrides?: Record<string, { title?: string; description?: string }>, attachments?: Record<string, File[]>) => Promise<{ created: number; errors: string[] }>;

  // Draft text
  pendingDraftText: string | null;
  setPendingDraftText: (text: string | null) => void;

  // Runs
  fetchRuns: (organizationId: string, limit?: number) => Promise<void>;
  triggerManualRun: (organizationId: string, chatJid?: string) => Promise<void>;

  // Client linking
  linkChatToClient: (chatJid: string, clientId: string, organizationId: string, chatName: string, configId: string) => Promise<void>;
  unlinkChatFromClient: (chatJid: string, organizationId: string) => Promise<void>;

  // Grouping & matching
  groupRequests: (requestIds: string[]) => Promise<string | null>;
  ungroupRequest: (requestId: string) => Promise<void>;
  linkRequestToTask: (requestId: string, taskId: string, taskTitle: string) => Promise<void>;
  unlinkRequestFromTask: (requestId: string, taskId: string) => Promise<void>;
  forwardToTeamChat: (requestId: string, teamChatId: string, note?: string) => Promise<boolean>;

  // AI Analysis
  fetchPendingSuggestionCounts: (organizationId: string) => Promise<void>;
  fetchChatSummaries: (organizationId: string) => Promise<void>;
  triggerSingleAnalysis: (organizationId: string, chatJid: string) => Promise<void>;
  smartAutoAnalyze: (organizationId: string, configId: string) => Promise<void>;
  cleanupAnalysisSubscription: () => void;

  // Utility
  fetchDistinctChats: (configId: string) => Promise<Array<{ chat_jid: string; chat_name: string | null; chat_type: string; message_count: number; last_message_at: string }>>;
  clear: () => void;
}

export const useWhatsAppAgentStore = create<WhatsAppAgentStore>((set, get) => ({
  assignments: [],
  requests: [],
  runs: [],
  loading: false,
  error: null,
  chatSuggestions: [],
  chatSuggestionsLoading: false,
  analysisStatus: new Map(),
  pendingSuggestionCounts: new Map(),
  chatSummaries: new Map(),
  pendingDraftText: null,
  setPendingDraftText: (text) => set({ pendingDraftText: text }),

  clear: () => {
    _activeAnalysisChannel?.unsubscribe();
    _activeAnalysisChannel = null;
    set({ assignments: [], requests: [], runs: [], chatSuggestions: [], analysisStatus: new Map(), pendingSuggestionCounts: new Map(), chatSummaries: new Map(), error: null });
  },

  // ---- ASSIGNMENTS ----

  fetchAssignments: async (organizationId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('whatsapp_chat_assignments')
        .select(`
          *,
          client:clients(
            id, business_name, stato_cliente,
            pipeline_stage:lead_pipeline_stages!pipeline_stage_id(id, name, color)
          ),
          deal:deals(id, title),
          project:projects(id, name, project_manager_id)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Risolvi client_state lato client (stato_cliente è text, non FK)
      const { getCachedClientStates } = await import('./referenceDataCache');
      const clientStates = await getCachedClientStates(organizationId);
      const stateMap = new Map(clientStates?.map(s => [s.id, s]) || []);

      const enriched = (data || []).map(a => ({
        ...a,
        client: a.client ? {
          ...a.client,
          client_state: a.client.stato_cliente ? stateMap.get(a.client.stato_cliente) || null : null,
        } : null,
      }));

      console.log(`[WhatsAppAgentStore] fetchAssignments: ${enriched.length} assignments loaded`, enriched.map(a => ({ jid: a.chat_jid, client: a.client?.business_name || '(none)', client_id: a.client_id })));
      set({ assignments: enriched, loading: false });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchAssignments error:', err);
      set({ error: err.message, loading: false });
    }
  },

  createAssignment: async (data: NewChatAssignment) => {
    try {
      const { data: created, error } = await supabase
        .from('whatsapp_chat_assignments')
        .insert(data)
        .select(`
          *,
          client:clients(id, business_name),
          deal:deals(id, title),
          project:projects(id, name, project_manager_id)
        `)
        .single();

      if (error) throw error;

      set((state) => ({
        assignments: [created, ...state.assignments]
      }));
      return created;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] createAssignment error:', err);
      set({ error: err.message });
      return null;
    }
  },

  updateAssignment: async (id: string, data: Partial<ChatAssignment>) => {
    try {
      const { updated_at, created_at, client, deal, project, ...updateData } = data as any;
      const { error } = await supabase
        .from('whatsapp_chat_assignments')
        .update({ ...updateData, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        assignments: state.assignments.map((a) =>
          a.id === id ? { ...a, ...data, updated_at: new Date().toISOString() } : a
        )
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] updateAssignment error:', err);
      set({ error: err.message });
    }
  },

  deleteAssignment: async (id: string) => {
    try {
      const { error } = await supabase
        .from('whatsapp_chat_assignments')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        assignments: state.assignments.filter((a) => a.id !== id)
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] deleteAssignment error:', err);
      set({ error: err.message });
    }
  },

  // ---- CLIENT LINKING ----

  linkChatToClient: async (chatJid: string, clientId: string, organizationId: string, chatName: string, configId: string) => {
    try {
      console.log('[WhatsAppAgentStore] linkChatToClient:', { chatJid, clientId, organizationId, configId: configId || '(EMPTY!)' });

      // 1. Cerca assignment esistente per aggiornarlo direttamente (più affidabile dell'upsert)
      const existing = get().assignments.find(
        a => a.chat_jid === chatJid && a.organization_id === organizationId
      );

      if (existing) {
        // UPDATE diretto
        console.log('[WhatsAppAgentStore] Updating existing assignment:', existing.id);
        const { error } = await supabase
          .from('whatsapp_chat_assignments')
          .update({
            client_id: clientId,
            chat_name: chatName,
            assignment_source: 'manual' as const,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);
        if (error) {
          console.error('[WhatsAppAgentStore] Update assignment error:', error);
          throw error;
        }
      } else {
        // INSERT nuovo (o upsert come safety net per race condition store vuoto / DB con dati)
        console.log('[WhatsAppAgentStore] Creating new assignment via upsert');
        const { error } = await supabase
          .from('whatsapp_chat_assignments')
          .upsert({
            organization_id: organizationId,
            config_id: configId,
            chat_jid: chatJid,
            chat_name: chatName,
            chat_type: chatJid.endsWith('@g.us') ? 'group' : 'individual',
            client_id: clientId,
            assignment_source: 'manual' as const,
            updated_at: new Date().toISOString(),
          }, {
            onConflict: 'organization_id,chat_jid',
            ignoreDuplicates: false,
          });
        if (error) {
          console.error('[WhatsAppAgentStore] Upsert assignment error:', error);
          throw error;
        }
      }

      // 2. Refresh assignments PRIMA di tentare il contatto (così l'UI si aggiorna subito)
      await get().fetchAssignments(organizationId);

      // 3. Aggiungi client_contact se il numero non esiste già (fire-and-forget, non blocca l'UI)
      try {
        const jidNumber = chatJid.split('@')[0].replace(/\D/g, '');
        if (jidNumber && !chatJid.endsWith('@g.us')) {
          const formattedPhone = jidNumber.startsWith('39') && jidNumber.length >= 11
            ? `+${jidNumber}`
            : `+${jidNumber}`;

          const { data: existingContacts } = await supabase
            .from('client_contacts')
            .select('id, phone')
            .eq('client_id', clientId)
            .eq('is_active', true);

          const normalizedJid = jidNumber;
          const alreadyExists = existingContacts?.some((c) => {
            const normalized = (c.phone || '').replace(/\D/g, '');
            return normalized.endsWith(normalizedJid) || normalizedJid.endsWith(normalized);
          });

          if (!alreadyExists) {
            // Modello Contatti first-class: il referente WhatsApp diventa una persona
            // clients(individual) + link client_relationships('contatto').
            await persistClientReferenti(clientId, organizationId, [{
              name: chatName || `WhatsApp ${formattedPhone}`,
              phone: formattedPhone,
              area: 'altro',
              is_primary: false,
            }] as any);
          }
        }
      } catch (contactErr) {
        console.warn('[WhatsAppAgentStore] client_contact insert error (non-blocking):', contactErr);
      }
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] linkChatToClient error:', err);
      set({ error: err.message });
    }
  },

  unlinkChatFromClient: async (chatJid: string, organizationId: string) => {
    try {
      const existing = get().assignments.find(a => a.chat_jid === chatJid && a.organization_id === organizationId);
      if (!existing) return;

      const { error } = await supabase
        .from('whatsapp_chat_assignments')
        .update({ client_id: null, updated_at: new Date().toISOString() })
        .eq('id', existing.id);

      if (error) throw error;

      // Aggiorna localmente
      set((state) => ({
        assignments: state.assignments.map((a) =>
          a.id === existing.id ? { ...a, client_id: null, client: null } : a
        ),
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] unlinkChatFromClient error:', err);
      set({ error: err.message });
    }
  },

  // ---- REQUESTS ----

  fetchRequests: async (organizationId: string, filters?: AgentRequestFilters) => {
    set({ loading: true, error: null });
    try {
      let query = supabase
        .from('whatsapp_agent_requests')
        .select(`
          *,
          assignment:whatsapp_chat_assignments(id, chat_jid, chat_name, chat_type),
          created_task:tasks(id, title, status)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (filters?.status) query = query.eq('status', filters.status);
      if (filters?.request_type) query = query.eq('request_type', filters.request_type);
      if (filters?.project_id) query = query.eq('project_id', filters.project_id);
      if (filters?.assignment_id) query = query.eq('assignment_id', filters.assignment_id);

      const { data, error } = await query;
      if (error) throw error;
      set({ requests: data || [], loading: false });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchRequests error:', err);
      set({ error: err.message, loading: false });
    }
  },

  fetchProjectRequests: async (projectId: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('whatsapp_agent_requests')
        .select(`
          *,
          assignment:whatsapp_chat_assignments(id, chat_jid, chat_name, chat_type),
          created_task:tasks(id, title, status)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      set({ requests: data || [], loading: false });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchProjectRequests error:', err);
      set({ error: err.message, loading: false });
    }
  },

  updateRequestStatus: async (id: string, status: AgentRequestStatus, reviewedBy?: string) => {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };
      if (reviewedBy) {
        updateData.reviewed_by = reviewedBy;
        updateData.reviewed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('whatsapp_agent_requests')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id ? { ...r, ...updateData } : r
        )
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] updateRequestStatus error:', err);
      set({ error: err.message });
    }
  },

  createTaskFromRequest: async (requestId: string, projectId: string, assignTo?: string) => {
    try {
      const request = get().requests.find((r) => r.id === requestId);
      if (!request) throw new Error('Request not found');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      // Determine priority based on request type
      const priorityMap: Record<string, string> = {
        bug_report: 'high',
        support_request: 'medium',
        feature_request: 'medium',
        task_request: 'medium',
        status_inquiry: 'low'
      };

      const { data: task, error: taskError } = await supabase
        .from('tasks')
        .insert({
          project_id: projectId,
          title: request.title,
          description: `[Richiesta da WhatsApp - ${request.request_type}]\n\n${request.description}\n\n---\nMessaggio originale: ${request.original_text}\nMittente: ${request.sender_name || 'Sconosciuto'}`,
          status: 'todo',
          priority: priorityMap[request.request_type] || 'medium',
          section: 'backlog',
          is_client_request: true,
          assigned_to: assignTo || null,
          created_by: userData.user.id
        })
        .select('id, title, status')
        .single();

      if (taskError) throw taskError;

      // Update request with task reference
      const { error: updateError } = await supabase
        .from('whatsapp_agent_requests')
        .update({
          status: 'task_created',
          created_task_id: task.id,
          reviewed_by: userData.user.id,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: 'task_created' as const,
                created_task_id: task.id,
                created_task: task,
                reviewed_by: userData.user!.id,
                reviewed_at: new Date().toISOString()
              }
            : r
        )
      }));

      return task.id;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] createTaskFromRequest error:', err);
      set({ error: err.message });
      return null;
    }
  },

  dismissRequest: async (id: string, userId: string) => {
    await get().updateRequestStatus(id, 'dismissed', userId);
  },

  toggleScope: async (requestId: string, isOutOfScope: boolean, note?: string) => {
    try {
      const updateData: Record<string, any> = {
        is_out_of_scope: isOutOfScope,
        updated_at: new Date().toISOString()
      };
      if (note !== undefined) updateData.scope_note = note;

      const { error } = await supabase
        .from('whatsapp_agent_requests')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;

      set((state) => ({
        chatSuggestions: state.chatSuggestions.map((s) =>
          s.id === requestId ? { ...s, is_out_of_scope: isOutOfScope, scope_note: note ?? s.scope_note } : s
        ),
        requests: state.requests.map((r) =>
          r.id === requestId ? { ...r, is_out_of_scope: isOutOfScope, scope_note: note ?? r.scope_note } : r
        )
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] toggleScope error:', err);
      set({ error: err.message });
    }
  },

  sendClarification: async (requestId: string, message: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      // Send via WAHA using the existing send-response edge function
      const { error } = await supabase.functions.invoke('whatsapp-agent-send-response', {
        body: {
          request_id: requestId,
          custom_text: message,
          user_id: userData?.user?.id
        }
      });
      if (error) throw error;

      // Override status to waiting_clarification and store clarification data
      const now = new Date().toISOString();
      await supabase
        .from('whatsapp_agent_requests')
        .update({
          status: 'waiting_clarification',
          clarification_message: message,
          clarification_sent_at: now,
          updated_at: now
        })
        .eq('id', requestId);

      set((state) => ({
        chatSuggestions: state.chatSuggestions.map((s) =>
          s.id === requestId
            ? { ...s, status: 'waiting_clarification' as const, clarification_message: message, clarification_sent_at: now }
            : s
        ),
        requests: state.requests.map((r) =>
          r.id === requestId
            ? { ...r, status: 'waiting_clarification' as const, clarification_message: message, clarification_sent_at: now }
            : r
        )
      }));
      return true;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] sendClarification error:', err);
      set({ error: err.message });
      return false;
    }
  },

  // ---- CHAT SUGGESTIONS (for in-chat display) ----

  fetchChatSuggestions: async (chatJid: string, organizationId: string) => {
    set({ chatSuggestionsLoading: true });
    try {
      // Fetch all actionable suggestions for this chat:
      // - Unsent response suggestions (response_message present, not yet sent)
      // - Pending task requests (status 'new', task not yet created)
      const { data, error } = await supabase
        .from('whatsapp_agent_requests')
        .select('*, assignment:whatsapp_chat_assignments(id, chat_jid, chat_name, chat_type, project_id)')
        .eq('organization_id', organizationId)
        .eq('chat_jid', chatJid)
        .not('status', 'in', '("dismissed","duplicate")')
        .or('and(response_message.not.is.null,response_sent_at.is.null),status.eq.new,status.eq.waiting_clarification')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      set({ chatSuggestions: data || [], chatSuggestionsLoading: false });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchChatSuggestions error:', err);
      set({ chatSuggestions: [], chatSuggestionsLoading: false });
    }
  },

  sendSuggestion: async (requestId: string, customText?: string) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.functions.invoke('whatsapp-agent-send-response', {
        body: {
          request_id: requestId,
          custom_text: customText || undefined,
          user_id: userData?.user?.id
        }
      });
      if (error) throw error;

      // Remove from local chatSuggestions
      set((state) => ({
        chatSuggestions: state.chatSuggestions.filter((s) => s.id !== requestId)
      }));
      return true;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] sendSuggestion error:', err);
      set({ error: err.message });
      return false;
    }
  },

  dismissSuggestion: async (requestId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from('whatsapp_agent_requests')
        .update({
          status: 'dismissed',
          reviewed_by: userId,
          reviewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      set((state) => ({
        chatSuggestions: state.chatSuggestions.filter((s) => s.id !== requestId)
      }));
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] dismissSuggestion error:', err);
      set({ error: err.message });
    }
  },

  createTasksFromSuggestions: async (requestIds: string[], projectId: string, assignTo?: string, overrides?: Record<string, { title?: string; description?: string }>, attachments?: Record<string, File[]>) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return { created: 0, errors: ['Utente non autenticato'] };

    const suggestions = get().chatSuggestions;
    const results: { created: number; errors: string[] } = { created: 0, errors: [] };
    const createdIds: string[] = [];

    const priorityMap: Record<string, string> = {
      bug_report: 'high',
      support_request: 'medium',
      feature_request: 'medium',
      task_request: 'medium',
      status_inquiry: 'low',
    };

    for (const requestId of requestIds) {
      try {
        const request = suggestions.find((s) => s.id === requestId);
        if (!request) {
          results.errors.push(`Request ${requestId} non trovata`);
          continue;
        }

        const taskTitle = overrides?.[requestId]?.title || request.title;
        const taskDescription = overrides?.[requestId]?.description || request.description;

        const { data: task, error: taskError } = await supabase
          .from('tasks')
          .insert({
            project_id: projectId,
            title: taskTitle,
            description: `[Richiesta da WhatsApp - ${request.request_type}]\n\n${taskDescription}\n\n---\nMessaggio originale: ${request.original_text}\nMittente: ${request.sender_name || 'Sconosciuto'}`,
            status: 'todo',
            priority: priorityMap[request.request_type] || 'medium',
            section: 'backlog',
            is_client_request: true,
            assigned_to: assignTo || null,
            created_by: userData.user.id,
          })
          .select('id')
          .single();

        if (taskError) {
          results.errors.push(`"${taskTitle}": ${taskError.message}`);
          continue;
        }

        await supabase
          .from('whatsapp_agent_requests')
          .update({
            status: 'task_created',
            created_task_id: task.id,
            reviewed_by: userData.user.id,
            reviewed_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', requestId);

        // Upload allegati immagini se presenti
        const taskFiles = attachments?.[requestId] || [];
        for (const file of taskFiles) {
          try {
            const filename = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
            const path = `tasks/${task.id}/${filename}`;
            const { error: uploadError } = await supabase.storage
              .from('documents')
              .upload(path, file, { upsert: false });

            if (uploadError) {
              console.error('[WhatsAppAgentStore] Attachment upload error:', uploadError);
              continue;
            }

            const { data: { publicUrl } } = supabase.storage
              .from('documents')
              .getPublicUrl(path);

            await supabase.from('task_attachments').insert({
              task_id: task.id,
              name: file.name,
              url: publicUrl,
              type: file.type || 'image/jpeg',
              size: file.size,
              created_by: userData.user.id,
            });
          } catch (attachErr) {
            console.error('[WhatsAppAgentStore] Attachment error:', attachErr);
          }
        }

        createdIds.push(requestId);
        results.created++;
      } catch (err: any) {
        results.errors.push(err.message);
      }
    }

    if (createdIds.length > 0) {
      set((state) => ({
        chatSuggestions: state.chatSuggestions.filter((s) => !createdIds.includes(s.id)),
      }));
    }

    return results;
  },

  // ---- RUNS ----

  fetchRuns: async (organizationId: string, limit = 20) => {
    try {
      const { data, error } = await supabase
        .from('whatsapp_agent_runs')
        .select('*')
        .eq('organization_id', organizationId)
        .order('started_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      set({ runs: data || [] });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchRuns error:', err);
      set({ error: err.message });
    }
  },

  triggerManualRun: async (organizationId: string, chatJid?: string) => {
    // Interruttore IA dell'azienda: spento = nessun avvio dell'agente.
    if (!isOrgAiEnabledNow()) { notifyAiDisabled(); return; }
    try {
      const { error } = await supabase.functions.invoke('whatsapp-agent-analyze', {
        body: { organization_id: organizationId, force: true, chat_jid: chatJid }
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] triggerManualRun error:', err);
      set({ error: err.message });
    }
  },

  // ---- AI ANALYSIS ----

  fetchPendingSuggestionCounts: async (organizationId: string) => {
    try {
      const { data, error } = await supabase
        .from('whatsapp_agent_requests')
        .select('chat_jid')
        .eq('organization_id', organizationId)
        .eq('status', 'new');

      if (error) throw error;

      const counts = new Map<string, number>();
      (data || []).forEach(r => {
        counts.set(r.chat_jid, (counts.get(r.chat_jid) || 0) + 1);
      });
      set({ pendingSuggestionCounts: counts });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchPendingSuggestionCounts error:', err);
    }
  },

  fetchChatSummaries: async (organizationId: string) => {
    try {
      const { data, error } = await supabase
        .from('whatsapp_chat_assignments')
        .select('chat_jid, ai_summary')
        .eq('organization_id', organizationId)
        .not('ai_summary', 'is', null);

      if (error) throw error;

      const summaries = new Map<string, AiSummary>();
      (data || []).forEach(a => {
        if (a.ai_summary) {
          summaries.set(a.chat_jid, a.ai_summary as AiSummary);
        }
      });
      set({ chatSummaries: summaries });
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchChatSummaries error:', err);
    }
  },

  triggerSingleAnalysis: async (organizationId: string, chatJid: string) => {
    // Interruttore IA dell'azienda: spento = nessuna analisi.
    if (!isOrgAiEnabledNow()) { notifyAiDisabled(); return; }
    // Setta stato analyzing per questa chat
    const statusMap = new Map(get().analysisStatus);
    statusMap.set(chatJid, 'analyzing');
    set({ analysisStatus: statusMap });

    // Subscribe a Realtime per risultati in tempo reale PRIMA di invocare
    _activeAnalysisChannel?.unsubscribe();
    const channelName = `agent-results-${chatJid.replace(/[@.]/g, '-')}-${Date.now()}`;
    _activeAnalysisChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'whatsapp_agent_requests',
          filter: `chat_jid=eq.${chatJid}`,
        },
        (payload) => {
          // Append nuova suggestion in tempo reale
          const newRequest = payload.new as AgentRequest;
          if (!newRequest) return;

          // Aggiorna sempre il conteggio badge (funziona anche se cambi chat)
          const counts = new Map(get().pendingSuggestionCounts);
          counts.set(chatJid, (counts.get(chatJid) || 0) + 1);

          // Appenda a chatSuggestions solo se l'utente sta ancora guardando questa chat
          const currentChatId = useWhatsAppChatStore.getState().selectedChatId;
          if (currentChatId === chatJid) {
            set(state => ({
              chatSuggestions: [...state.chatSuggestions, newRequest],
              pendingSuggestionCounts: counts,
            }));
          } else {
            set({ pendingSuggestionCounts: counts });
          }
        }
      )
      .subscribe();

    // Safety timeout: cleanup dopo 30s
    const timeoutId = setTimeout(() => {
      _activeAnalysisChannel?.unsubscribe();
      _activeAnalysisChannel = null;
    }, 30_000);

    try {
      // config_id e nome: servono alla funzione per analizzare anche una chat mai collegata
      // a Lead/Cliente o progetto (crea la riga con l'agente spento).
      const chatState = useWhatsAppChatStore.getState();
      const chatName = chatState.chats.find(c => c.id === chatJid)?.name;
      const { data, error } = await supabase.functions.invoke('whatsapp-agent-analyze', {
        body: {
          organization_id: organizationId,
          force: true,
          chat_jid: chatJid,
          config_id: chatState.wahaConfig?.configId,
          chat_name: chatName,
        }
      });

      if (error) throw error;

      // Estrai summary dal risultato
      const chatResult = data?.results?.details?.[chatJid];
      if (chatResult?.summary) {
        const summaries = new Map(get().chatSummaries);
        summaries.set(chatJid, {
          text: chatResult.summary.text,
          nextSteps: chatResult.summary.nextSteps || [],
          generatedAt: new Date().toISOString(),
          lastMessageId: '',
          requestsCount: chatResult.requestsExtracted || 0,
        });
        set({ chatSummaries: summaries });
      }

      // Aggiorna stato a done
      const doneMap = new Map(get().analysisStatus);
      doneMap.set(chatJid, 'done');
      set({ analysisStatus: doneMap });

      // Chat senza riga di collegamento: la funzione l'ha appena creata, va ricaricata.
      if (!get().assignments.some(a => a.chat_jid === chatJid)) {
        void get().fetchAssignments(organizationId);
      }

      // Re-fetch suggestions complete (la subscription potrebbe aver perso qualcosa)
      get().fetchChatSuggestions(chatJid, organizationId);
      get().fetchPendingSuggestionCounts(organizationId);

    } catch (err: any) {
      console.error('[WhatsAppAgentStore] triggerSingleAnalysis error:', err);
      const errMap = new Map(get().analysisStatus);
      errMap.delete(chatJid);
      set({ analysisStatus: errMap, error: err.message });
    } finally {
      clearTimeout(timeoutId);
      _activeAnalysisChannel?.unsubscribe();
      _activeAnalysisChannel = null;
    }
  },

  smartAutoAnalyze: async (organizationId: string, configId: string) => {
    // Esegui solo una volta per sessione
    if (_smartAnalyzeRan) return;
    // Interruttore IA dell'azienda: spento = niente analisi automatica (senza avvisi).
    if (!isOrgAiEnabledNow()) return;
    _smartAnalyzeRan = true;

    try {
      // 1. Carica assignments con agent abilitato
      const { data: assignments, error: assignErr } = await supabase
        .from('whatsapp_chat_assignments')
        .select('id, chat_jid, last_analyzed_at, agent_response_mode')
        .eq('organization_id', organizationId)
        .neq('agent_response_mode', 'off');

      if (assignErr || !assignments || assignments.length === 0) return;

      // 2. Per ogni assignment, controlla se ci sono messaggi inbound nuovi non risposti
      const chatJids = assignments.map(a => a.chat_jid);
      const cutoffDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // ultimi 7 giorni

      // Query ultimi messaggi per queste chat
      const { data: recentMessages } = await supabase
        .from('whatsapp_messages')
        .select('from_jid, to_jid, direction, created_at')
        .eq('config_id', configId)
        .gt('created_at', cutoffDate)
        .order('created_at', { ascending: false })
        .limit(500); // max 500 messaggi recenti

      if (!recentMessages || recentMessages.length === 0) return;

      // Raggruppa per chat_jid e trova quelle da analizzare
      const chatsToAnalyze: string[] = [];

      for (const assignment of assignments) {
        const chatJidClean = assignment.chat_jid.split('@')[0];
        // Filtra messaggi per questa chat
        const chatMsgs = recentMessages.filter(m =>
          m.from_jid?.includes(chatJidClean) || m.to_jid?.includes(chatJidClean)
        );

        if (chatMsgs.length === 0) continue;

        // L'ultimo messaggio è inbound? (non abbiamo risposto)
        const lastMsg = chatMsgs[0]; // già ordinati desc
        if (lastMsg.direction !== 'inbound') continue;

        // Conta messaggi nuovi dopo last_analyzed_at
        const lastAnalyzed = assignment.last_analyzed_at || '1970-01-01';
        const newInbound = chatMsgs.filter(m =>
          m.direction === 'inbound' && m.created_at > lastAnalyzed
        );

        // Threshold: almeno 3 messaggi inbound nuovi
        if (newInbound.length >= 3) {
          chatsToAnalyze.push(assignment.chat_jid);
        }
      }

      if (chatsToAnalyze.length === 0) {
        console.log('[WhatsAppAgentStore] smartAutoAnalyze: nessuna chat da analizzare');
        return;
      }

      console.log(`[WhatsAppAgentStore] smartAutoAnalyze: ${chatsToAnalyze.length} chat da analizzare`);

      // 3. Triggera analisi (max 3 in parallelo)
      const batchSize = 3;
      for (let i = 0; i < chatsToAnalyze.length; i += batchSize) {
        const batch = chatsToAnalyze.slice(i, i + batchSize);
        await Promise.allSettled(
          batch.map(chatJid => get().triggerSingleAnalysis(organizationId, chatJid))
        );
      }

    } catch (err: any) {
      console.error('[WhatsAppAgentStore] smartAutoAnalyze error:', err);
    }
  },

  cleanupAnalysisSubscription: () => {
    _activeAnalysisChannel?.unsubscribe();
    _activeAnalysisChannel = null;
  },

  // ---- UTILITY ----

  fetchDistinctChats: async (configId: string) => {
    try {
      // Get distinct chats from whatsapp_messages with counts
      const { data, error } = await supabase.rpc('get_distinct_whatsapp_chats', {
        p_config_id: configId
      });

      if (error) {
        // Fallback: simple query if RPC not available
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('whatsapp_messages')
          .select('from_jid, created_at')
          .eq('config_id', configId)
          .eq('direction', 'inbound')
          .order('created_at', { ascending: false })
          .limit(500);

        if (fallbackError) throw fallbackError;

        // Group by from_jid manually
        const chatMap = new Map<string, { count: number; last: string }>();
        for (const msg of fallbackData || []) {
          const jid = msg.from_jid;
          const existing = chatMap.get(jid);
          if (!existing) {
            chatMap.set(jid, { count: 1, last: msg.created_at });
          } else {
            existing.count++;
          }
        }

        return Array.from(chatMap.entries()).map(([jid, info]) => ({
          chat_jid: jid,
          chat_name: null,
          chat_type: jid.includes('@g.us') ? 'group' : 'individual',
          message_count: info.count,
          last_message_at: info.last
        }));
      }

      return data || [];
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] fetchDistinctChats error:', err);
      return [];
    }
  },

  // ---- GROUPING & MATCHING ----

  groupRequests: async (requestIds: string[]) => {
    if (requestIds.length < 2) return null;
    const groupId = crypto.randomUUID();
    const { error } = await supabase
      .from('whatsapp_agent_requests')
      .update({ group_id: groupId })
      .in('id', requestIds);
    if (error) { console.error('[WhatsAppAgentStore] groupRequests error:', error); return null; }
    // Update local state
    set(state => ({
      requests: state.requests.map(r => requestIds.includes(r.id) ? { ...r, group_id: groupId } : r),
      chatSuggestions: state.chatSuggestions.map(r => requestIds.includes(r.id) ? { ...r, group_id: groupId } : r),
    }));
    return groupId;
  },

  ungroupRequest: async (requestId: string) => {
    const { error } = await supabase
      .from('whatsapp_agent_requests')
      .update({ group_id: null })
      .eq('id', requestId);
    if (error) { console.error('[WhatsAppAgentStore] ungroupRequest error:', error); return; }
    set(state => ({
      requests: state.requests.map(r => r.id === requestId ? { ...r, group_id: null } : r),
      chatSuggestions: state.chatSuggestions.map(r => r.id === requestId ? { ...r, group_id: null } : r),
    }));
  },

  linkRequestToTask: async (requestId: string, taskId: string, taskTitle: string) => {
    // Get current matched_tasks and add to array
    const request = get().requests.find(r => r.id === requestId) || get().chatSuggestions.find(r => r.id === requestId);
    const existing = request?.matched_tasks || [];
    if (existing.some(t => t.id === taskId)) return; // Already linked
    const updated = [...existing, { id: taskId, title: taskTitle, status: 'unknown' }];
    const { error } = await supabase
      .from('whatsapp_agent_requests')
      .update({
        matched_tasks: updated,
        matched_task_id: updated[0]?.id || null,
        matched_task_title: updated[0]?.title || null,
      })
      .eq('id', requestId);
    if (error) { console.error('[WhatsAppAgentStore] linkRequestToTask error:', error); return; }
    const updater = (r: AgentRequest) => r.id === requestId
      ? { ...r, matched_tasks: updated, matched_task_id: updated[0]?.id || null, matched_task_title: updated[0]?.title || null }
      : r;
    set(state => ({
      requests: state.requests.map(updater),
      chatSuggestions: state.chatSuggestions.map(updater),
    }));
  },

  unlinkRequestFromTask: async (requestId: string, taskId: string) => {
    const request = get().requests.find(r => r.id === requestId) || get().chatSuggestions.find(r => r.id === requestId);
    const existing = request?.matched_tasks || [];
    const updated = existing.filter(t => t.id !== taskId);
    const { error } = await supabase
      .from('whatsapp_agent_requests')
      .update({
        matched_tasks: updated.length > 0 ? updated : null,
        matched_task_id: updated[0]?.id || null,
        matched_task_title: updated[0]?.title || null,
      })
      .eq('id', requestId);
    if (error) { console.error('[WhatsAppAgentStore] unlinkRequestFromTask error:', error); return; }
    const updater = (r: AgentRequest) => r.id === requestId
      ? { ...r, matched_tasks: updated.length > 0 ? updated : null, matched_task_id: updated[0]?.id || null, matched_task_title: updated[0]?.title || null }
      : r;
    set(state => ({
      requests: state.requests.map(updater),
      chatSuggestions: state.chatSuggestions.map(updater),
    }));
  },

  forwardToTeamChat: async (requestId: string, teamChatId: string, note?: string) => {
    try {
      const request = get().requests.find(r => r.id === requestId) || get().chatSuggestions.find(r => r.id === requestId);
      if (!request) return false;

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      // Structured JSON content for whatsapp_forward message type
      const forwardData = {
        request_type: request.request_type,
        title: request.title,
        description: request.description || null,
        original_text: request.original_text?.substring(0, 500) || null,
        sender_name: request.sender_name || null,
        ai_confidence: request.ai_confidence,
        matched_tasks: request.matched_tasks || null,
        chat_jid: request.chat_jid,
        note: note || null,
      };

      // Insert with special message_type for custom rendering
      const { error } = await supabase.from('team_chat_messages').insert({
        chat_id: teamChatId,
        sender_id: userData.user.id,
        content: JSON.stringify(forwardData),
        message_type: 'whatsapp_forward',
      });

      if (error) throw error;
      return true;
    } catch (err: any) {
      console.error('[WhatsAppAgentStore] forwardToTeamChat error:', err);
      return false;
    }
  },
}));
